import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { TOP_STRIP, CAPABILITY_LABELS, PHONE_DISPLAY, PHONE_LINK, LOGO_BLACK, BUSINESS, scrollToId, openInquiry, slug } from "@/lib/site";
import { PrimaryButton } from "@/components/site/Buttons";
import { Reveal } from "@/components/site/motion";

export const TopMarquee = () => {
  const items = [...TOP_STRIP, ...TOP_STRIP, ...TOP_STRIP, ...TOP_STRIP];
  return (
    <div data-testid="trust-marquee" className="marquee-track relative overflow-hidden py-3 select-none">
      <span className="texture-green absolute inset-0" aria-hidden="true" />
      <div className="marquee-inner relative flex w-max animate-marquee-slow">
        {items.map((item, i) => (
          <span key={i} className="flex items-center whitespace-nowrap">
            <span className="font-mono font-medium text-[11px] sm:text-xs tracking-[0.24em] text-white px-8">{item}</span>
            <span className="w-1.5 h-1.5 rotate-45 bg-sun shrink-0" />
          </span>
        ))}
      </div>
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header data-testid="top-header" className={`sticky top-0 z-40 transition-[background-color,border-color] duration-500 ${scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-charcoal/[0.06]" : "bg-cream border-b border-transparent"}`}>
      <div className="flex sm:grid sm:grid-cols-[1fr_auto_1fr] items-center justify-between gap-3 px-4 sm:px-8 py-3.5 max-w-[1700px] mx-auto">
        <a data-testid="header-phone" href={PHONE_LINK} className="hidden sm:block justify-self-start font-mono text-[11px] tracking-[0.16em] text-charcoal/70 hover:text-leaf transition-colors duration-300">
          {PHONE_DISPLAY}
        </a>
        <button type="button" onClick={() => scrollToId("home")} className="sm:justify-self-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf rounded-md" aria-label={`${BUSINESS} home`}>
          <img src={LOGO_BLACK} alt={BUSINESS} data-testid="header-logo" className="h-6 min-[400px]:h-7 sm:h-9 lg:h-11 w-auto transition-transform duration-500 hover:scale-[1.03]" />
        </button>
        <div className="justify-self-end">
          <PrimaryButton small testid="send-inquiry-header-button" className="!px-4 sm:!px-5 !text-[10.5px] sm:!text-xs" onClick={() => openInquiry()}>
            <span>DISCUSS MY PROJECT</span>
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </PrimaryButton>
        </div>
      </div>
    </header>
  );
};

const LABEL_STYLES = [
  "font-display font-semibold tracking-tight",
  "font-mono font-medium tracking-[0.14em]",
  "font-serif italic",
  "font-body font-semibold tracking-tight",
  "font-mono font-bold",
  "font-display font-medium tracking-wide",
  "font-body font-medium tracking-[0.08em]",
  "font-display font-semibold",
  "font-mono font-medium tracking-[0.2em]",
];

export const CapabilitiesMarquee = () => {
  const items = [...CAPABILITY_LABELS, ...CAPABILITY_LABELS];
  return (
    <section id="range" data-testid="capabilities-marquee-section" className="pt-16 sm:pt-20 pb-16 sm:pb-20 overflow-hidden">
      <div className="px-6 sm:px-10 lg:px-16 max-w-[1700px] mx-auto mb-12">
        <Reveal>
          <p className="eyebrow mb-5">DESIGN, PLANTING & OUTDOOR LIVING</p>
          <h2 className="font-display font-medium tracking-[-0.02em] text-2xl sm:text-4xl lg:text-5xl text-charcoal max-w-3xl leading-[1.08]">
            A better yard starts with <span className="font-light">how you want to use it.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-charcoal/70 max-w-xl">
            Explore the features that could make your outdoor space more inviting, easier to navigate, and better suited to your daily life.
          </p>
        </Reveal>
      </div>
      <div className="marquee-track overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
        <div className="marquee-inner flex w-max animate-marquee-slower items-center">
          {items.map((label, i) => (
            <span
              key={i}
              data-testid={i < CAPABILITY_LABELS.length ? `capability-marquee-${slug(label)}` : undefined}
              className={`whitespace-nowrap px-10 sm:px-14 text-lg sm:text-2xl text-charcoal/55 hover:text-leaf transition-colors duration-500 cursor-default ${LABEL_STYLES[i % LABEL_STYLES.length]}`}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
